import {
  pixelPolylineLength,
  pixelPolygonArea,
  pixelsToFeet,
  pixelsSqToSF,
  type SheetScale,
} from '@otto/onscreen-engine';
import {
  assignBarMarks,
  createEstimateDefaults,
  deserializeJob,
  generateWeightSummary,
  rollupSegments,
  type BarMark,
  type FtIn,
  type Job,
  type Line,
  type Segment,
  type WeightSummaryEntry,
} from '@otto/rebar-engine';
import {
  restoreJobData,
  type CivilJobData,
  type ItemKind,
  type TakeoffItem,
} from '../takeoff-civil/helpers.ts';
import type {
  Submission,
  SubmissionPage,
  TakeoffSessionRow,
} from '../../lib/types.ts';

// ─── Civil aggregation ───────────────────────────────────────────────────────

export interface CivilItemRow {
  pageIndex: number;
  pageLabel: string;
  kind: ItemKind;
  label: string;
  quantity: number;
  unit: 'LF' | 'SF' | 'EA';
}

export interface CivilTotals {
  linearLF: number;
  polygonSF: number;
  count: number;
  itemCount: number;
}

export interface CivilPageBreakdown {
  pageIndex: number;
  pageLabel: string;
  scale: SheetScale | null;
  rows: CivilItemRow[];
  pageTotals: CivilTotals;
}

export interface CivilReport {
  pages: CivilPageBreakdown[];
  totals: CivilTotals;
  itemRows: CivilItemRow[];
}

function pageLabelFor(pages: readonly SubmissionPage[], pageIndex: number): string {
  const match = pages.find((p) => p.page_index === pageIndex);
  if (!match) return `Page ${pageIndex + 1}`;
  if (match.page_label) return `Page ${pageIndex + 1} · ${match.page_label}`;
  if (match.category) return `Page ${pageIndex + 1} · ${match.category}`;
  return `Page ${pageIndex + 1}`;
}

function quantityFor(item: TakeoffItem, scale: SheetScale | null): {
  quantity: number;
  unit: 'LF' | 'SF' | 'EA';
} {
  if (item.kind === 'count') {
    return { quantity: item.points.length, unit: 'EA' };
  }
  if (!scale) return { quantity: 0, unit: item.kind === 'linear' ? 'LF' : 'SF' };
  if (item.kind === 'linear') {
    return {
      quantity: pixelsToFeet(pixelPolylineLength(item.points), scale),
      unit: 'LF',
    };
  }
  return {
    quantity: pixelsSqToSF(pixelPolygonArea(item.points), scale),
    unit: 'SF',
  };
}

function emptyTotals(): CivilTotals {
  return { linearLF: 0, polygonSF: 0, count: 0, itemCount: 0 };
}

function addToTotals(t: CivilTotals, row: CivilItemRow): CivilTotals {
  if (row.kind === 'linear') return { ...t, linearLF: t.linearLF + row.quantity, itemCount: t.itemCount + 1 };
  if (row.kind === 'polygon') return { ...t, polygonSF: t.polygonSF + row.quantity, itemCount: t.itemCount + 1 };
  return { ...t, count: t.count + row.quantity, itemCount: t.itemCount + 1 };
}

export function buildCivilReport(
  session: TakeoffSessionRow | null,
  pages: readonly SubmissionPage[],
): CivilReport | null {
  if (!session || !session.job_data || Object.keys(session.job_data).length === 0) {
    return null;
  }
  const jobData: CivilJobData = restoreJobData(session.job_data);
  const pageEntries = Object.entries(jobData.pages)
    .map(([k, v]) => ({ pageIndex: Number(k), data: v }))
    .filter((e) => Number.isFinite(e.pageIndex))
    .sort((a, b) => a.pageIndex - b.pageIndex);

  const breakdowns: CivilPageBreakdown[] = [];
  let grand = emptyTotals();
  const allRows: CivilItemRow[] = [];

  for (const { pageIndex, data } of pageEntries) {
    const pageLabel = pageLabelFor(pages, pageIndex);
    const rows: CivilItemRow[] = [];
    let pageTotals = emptyTotals();
    data.items.forEach((item, i) => {
      const { quantity, unit } = quantityFor(item, data.scale);
      const row: CivilItemRow = {
        pageIndex,
        pageLabel,
        kind: item.kind,
        label: item.label?.trim() || `${item.kind} ${i + 1}`,
        quantity,
        unit,
      };
      rows.push(row);
      pageTotals = addToTotals(pageTotals, row);
      grand = addToTotals(grand, row);
    });
    if (rows.length > 0 || data.scale) {
      breakdowns.push({ pageIndex, pageLabel, scale: data.scale, rows, pageTotals });
      allRows.push(...rows);
    }
  }

  return { pages: breakdowns, totals: grand, itemRows: allRows };
}

// ─── Rebar aggregation ───────────────────────────────────────────────────────

export interface RebarLineRow {
  segmentName: string;
  lineNumber: number;
  material: string;
  function: string;
  size: string;
  class: string;
  quantity: number;
  multiplier: number;
  unitLengthFt: number;
  totalLengthFt: number;
  totalWeightLbs: number;
}

function unitLengthFeet(line: Line): number {
  const dims: FtIn[] = [];
  for (const d of [line.L1, line.L2, line.L3, line.L4, line.L5]) {
    if (d) dims.push(d);
  }
  if (dims.length === 0) return 0;
  return dims.reduce((sum, d) => sum + d.toDecimalFeet(), 0);
}

export interface RebarReport {
  segments: { name: string; lineCount: number; totalLbs: number }[];
  lineRows: RebarLineRow[];
  weightSummary: WeightSummaryEntry[];
  totalLbs: number;
  totalTons: number;
  segmentCount: number;
  lineCount: number;
}

export function buildRebarReport(
  session: TakeoffSessionRow | null,
): RebarReport | null {
  if (!session || !session.job_data || Object.keys(session.job_data).length === 0) {
    return null;
  }
  let job: Job;
  try {
    job = deserializeJob(JSON.stringify(session.job_data));
  } catch {
    return null;
  }
  const defaults = createEstimateDefaults();
  const cloned: Job = { ...job, segments: job.segments.map((s) => ({ ...s, lines: s.lines.map((l) => ({ ...l })) })) };
  const withMarks = assignBarMarks(cloned);
  const barMarks = new Map<string, BarMark>(withMarks.barMarks.map((m) => [m.barMarkId, m]));
  rollupSegments(withMarks.segments, barMarks, defaults);
  const allLines = withMarks.segments.flatMap((s: Segment) => s.lines);
  const summary = generateWeightSummary(allLines, defaults);
  const totalLbs = summary.reduce((sum, e) => sum + e.total, 0);
  const totalTons = summary.reduce((sum, e) => sum + e.tons, 0);

  const segments = withMarks.segments.map((s) => ({
    name: s.name?.trim() || s.segmentId,
    lineCount: s.lines.length,
    totalLbs: s.lines.reduce((sum, l) => sum + (l.weightLbs ?? 0), 0),
  }));

  const lineRows: RebarLineRow[] = [];
  for (const seg of withMarks.segments) {
    const segName = seg.name?.trim() || seg.segmentId;
    for (const line of seg.lines) {
      const unitFt = unitLengthFeet(line);
      lineRows.push({
        segmentName: segName,
        lineNumber: line.lineNumber,
        material: line.material,
        function: line.function,
        size: line.size?.toString() ?? '—',
        class: line.class ?? '—',
        quantity: line.quantity,
        multiplier: line.multiplier,
        unitLengthFt: unitFt,
        totalLengthFt: unitFt * line.quantity * line.multiplier,
        totalWeightLbs: line.weightLbs ?? 0,
      });
    }
  }

  return {
    segments,
    lineRows,
    weightSummary: summary,
    totalLbs,
    totalTons,
    segmentCount: withMarks.segments.length,
    lineCount: allLines.length,
  };
}

// ─── Combined ────────────────────────────────────────────────────────────────

export interface OutputData {
  submission: Submission;
  pages: readonly SubmissionPage[];
  civil: CivilReport | null;
  rebar: RebarReport | null;
  generatedAt: Date;
  preparedBy: string;
}

export function pickSession(
  rows: readonly TakeoffSessionRow[],
  discipline: 'structural' | 'civil',
): TakeoffSessionRow | null {
  return rows.find((r) => r.discipline === discipline) ?? null;
}

export function fmtNumber(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function fmtInt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString();
}

export function fmtScale(scale: SheetScale | null): string {
  if (!scale) return 'not calibrated';
  if (scale.scaleString) return scale.scaleString;
  return `${scale.pixelsPerFoot.toFixed(3)} px/ft (${scale.source})`;
}
